import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, PenLine, RotateCcw, AlertCircle, Clock, Users } from "lucide-react";
import staioIcon from "@/assets/images/staio-icon.png";

type ContractField = {
  id: string;
  label: string;
  type: "text" | "name" | "date" | "signature";
  required: boolean;
  placeholder?: string;
};

type SigningData = {
  session: {
    id: number;
    guestName: string | null;
    guestEmail: string | null;
    status: string;
    fieldValues: Record<string, string>;
  };
  contract: {
    id: number;
    title: string;
    body: string;
    fields: ContractField[];
  };
  orgName: string;
  orgLogoUrl?: string | null;
  signerInfo?: {
    position: number;
    total: number;
    name: string | null;
    email: string | null;
    signerStatus: string;
  } | null;
};

function interpolateBody(
  body: string,
  values: Record<string, string>,
  systemVars: Record<string, string>,
) {
  let result = body;
  Object.entries(systemVars).forEach(([key, val]) => {
    if (val) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "gi"), val);
    }
  });
  Object.entries(values).forEach(([key, val]) => {
    if (val && !val.startsWith('data:image')) {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "gi"), String(val));
    }
  });
  return result;
}

function SignaturePad({
  fieldId,
  onSign,
  onClear,
}: {
  fieldId: string;
  onSign: (dataUrl: string) => void;
  onClear: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const [hasSignature, setHasSignature] = useState(false);

  const getPoint = (e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    if (e instanceof TouchEvent) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * (canvas.width / rect.width), y: (t.clientY - rect.top) * (canvas.height / rect.height) };
    }
    return { x: (e.clientX - rect.left) * (canvas.width / rect.width), y: (e.clientY - rect.top) * (canvas.height / rect.height) };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fafaf9";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const startDraw = useCallback((e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current!;
    isDrawing.current = true;
    lastPoint.current = getPoint(e, canvas);
  }, []);

  const draw = useCallback((e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const point = getPoint(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPoint.current!.x, lastPoint.current!.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPoint.current = point;
    setHasSignature(true);
  }, []);

  const endDraw = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      onSign(dataUrl);
    }
  }, [onSign]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener("mousedown", startDraw);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", endDraw);
    canvas.addEventListener("mouseleave", endDraw);
    canvas.addEventListener("touchstart", startDraw, { passive: false });
    canvas.addEventListener("touchmove", draw, { passive: false });
    canvas.addEventListener("touchend", endDraw);
    return () => {
      canvas.removeEventListener("mousedown", startDraw);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", endDraw);
      canvas.removeEventListener("mouseleave", endDraw);
      canvas.removeEventListener("touchstart", startDraw);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", endDraw);
    };
  }, [startDraw, draw, endDraw]);

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fafaf9";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onClear();
  };

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed border-border rounded-lg overflow-hidden bg-stone-50 dark:bg-stone-950 relative">
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          className="w-full cursor-crosshair touch-none"
          data-testid={`canvas-signature-${fieldId}`}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-muted-foreground text-sm">Sign here</p>
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={clear}
        data-testid={`button-clear-signature-${fieldId}`}
      >
        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Clear
      </Button>
    </div>
  );
}

export default function SignContract() {
  const { token } = useParams<{ token: string }>();
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [signed, setSigned] = useState(false);

  const { data, isLoading, error } = useQuery<SigningData>({
    queryKey: ["/api/contract/sign", token],
    queryFn: () => fetch(`/api/contract/sign/${token}`).then((r) => {
      if (!r.ok) return r.json().then((e) => { throw new Error(e.message || "Failed"); });
      return r.json();
    }),
    retry: false,
  });

  useEffect(() => {
    if (data?.session?.fieldValues) {
      setFieldValues(data.session.fieldValues as Record<string, string>);
    }
    if (data?.session?.status === "signed") {
      setSigned(true);
    }
  }, [data]);

  const submitMutation = useMutation({
    mutationFn: (body: object) =>
      fetch(`/api/contract/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then((r) => {
        if (!r.ok) return r.json().then((e) => { throw new Error(e.message || "Failed"); });
        return r.json();
      }),
    onSuccess: () => setSigned(true),
  });

  const systemVars = useMemo(() => {
    const now = new Date();
    return {
      recipient_name: data?.session?.guestName || "",
      recipient_email: data?.session?.guestEmail || "",
      guest_name: data?.session?.guestName || "",
      org_name: data?.orgName || "",
      today_date: now.toLocaleDateString(),
      current_year: String(now.getFullYear()),
    };
  }, [data?.session?.guestName, data?.session?.guestEmail, data?.orgName]);

  const previewBody = useMemo(() => {
    if (!data) return "";
    return interpolateBody(data.contract.body, fieldValues, systemVars);
  }, [data, fieldValues, systemVars]);

  const handleSubmit = () => {
    if (!data) return;
    const newErrors: Record<string, string> = {};
    for (const field of data.contract.fields) {
      if (field.required) {
        const val = fieldValues[field.id];
        if (!val) {
          newErrors[field.id] = field.type === "signature" ? "Please draw your signature" : "This field is required";
        }
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const signatureFields = data.contract.fields.filter(f => f.type === "signature");
    const primarySignature = signatureFields.length > 0 ? fieldValues[signatureFields[0].id] || null : null;

    submitMutation.mutate({
      fieldValues,
      signatureImageData: primarySignature,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !data) {
    const msg = (error as Error)?.message || "This link is invalid or has expired.";
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h1 className="text-xl font-bold">Link Unavailable</h1>
          <p className="text-muted-foreground">{msg}</p>
        </div>
      </div>
    );
  }

  // Waiting for previous signer
  if (data?.session?.status === "waiting") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
        <div className="max-w-md text-center space-y-4 bg-white rounded-2xl p-8 shadow-md">
          <Clock className="h-16 w-16 text-amber-500 mx-auto" />
          <h1 className="text-2xl font-bold">Not Your Turn Yet</h1>
          <p className="text-muted-foreground">
            This document needs to be signed by the previous signer first. You will receive an email when it's your turn.
          </p>
          {data.signerInfo && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              You are signer {data.signerInfo.position} of {data.signerInfo.total}
            </div>
          )}
          <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
            <img src={staioIcon} alt="Staio" className="h-5" />
            <span>Powered by Staio</span>
          </div>
        </div>
      </div>
    );
  }

  if (signed) {
    const signerInfo = data?.signerInfo;
    const allDone = !signerInfo || signerInfo.position === signerInfo.total;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
        <div className="max-w-md text-center space-y-4 bg-white rounded-2xl p-8 shadow-md">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold">Document Signed</h1>
          <p className="text-muted-foreground">
            {allDone
              ? "Thank you! Your signature has been recorded. You may close this page."
              : `Thank you! Your signature has been recorded. The document has been automatically sent to the next signer.`}
          </p>
          {signerInfo && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <Users className="h-4 w-4 inline mr-1" />
              Signer {signerInfo.position} of {signerInfo.total} — complete
            </div>
          )}
          <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
            <img src={staioIcon} alt="Staio" className="h-5" />
            <span>Powered by Staio</span>
          </div>
        </div>
      </div>
    );
  }

  const { contract, session } = data;

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between mb-6">
          {data.orgLogoUrl ? (
            <img
              src={data.orgLogoUrl}
              alt={data.orgName || "Organization"}
              className="h-10 max-w-[180px] object-contain"
              data-testid="img-org-logo"
            />
          ) : (
            <div className="flex items-center gap-2">
              <img src={staioIcon} alt="Staio" className="h-7" />
              <span className="text-sm text-muted-foreground">Document Signing</span>
            </div>
          )}
          {data.orgLogoUrl && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <img src={staioIcon} alt="Staio" className="h-4 opacity-60" />
              <span>Powered by Staio</span>
            </div>
          )}
        </div>

        {data.signerInfo && data.signerInfo.total > 1 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-800">
            <Users className="h-4 w-4 shrink-0" />
            <span>You are signer <strong>{data.signerInfo.position}</strong> of <strong>{data.signerInfo.total}</strong> — review and sign below.</span>
          </div>
        )}

        <div className="bg-white dark:bg-stone-900 rounded-2xl shadow-sm border p-6 space-y-4">
          <h1 className="text-xl font-bold" data-testid="text-contract-title">
            {contract.title}
          </h1>
          {session.guestName && (
            <p className="text-sm text-muted-foreground">
              Hello, <strong>{session.guestName}</strong>. Please review the document below and complete the required fields.
            </p>
          )}

          <div
            className="prose prose-sm max-w-none text-sm leading-relaxed text-foreground border rounded-lg p-4 bg-stone-50 dark:bg-stone-800 whitespace-pre-wrap font-mono"
            data-testid="text-contract-body"
          >
            {previewBody}
          </div>
        </div>

        {contract.fields.length > 0 && (
          <div className="bg-white dark:bg-stone-900 rounded-2xl shadow-sm border p-6 space-y-5">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
              Required Information
            </h2>
            {contract.fields.map((field) => (
              <div key={field.id} className="space-y-1.5">
                {field.type === "signature" ? (
                  <>
                    <Label>
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      By signing below, you acknowledge that you have read and agree to the document above.
                    </p>
                    <SignaturePad
                      fieldId={field.id}
                      onSign={(dataUrl) => {
                        setFieldValues((p) => ({ ...p, [field.id]: dataUrl }));
                        setErrors((p) => ({ ...p, [field.id]: "" }));
                      }}
                      onClear={() =>
                        setFieldValues((p) => {
                          const next = { ...p };
                          delete next[field.id];
                          return next;
                        })
                      }
                    />
                  </>
                ) : (
                  <>
                    <Label htmlFor={field.id}>
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    <Input
                      id={field.id}
                      type={field.type === "date" ? "date" : "text"}
                      placeholder={field.placeholder || (field.type === "name" ? "Enter your full name" : undefined)}
                      value={(fieldValues[field.id] as string) || ""}
                      onChange={(e) =>
                        setFieldValues((p) => ({ ...p, [field.id]: e.target.value }))
                      }
                      data-testid={`input-${field.id}`}
                    />
                  </>
                )}
                {errors[field.id] && (
                  <p className="text-xs text-destructive">{errors[field.id]}</p>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="pb-8">
          <Button
            className="w-full h-12 text-base"
            onClick={handleSubmit}
            disabled={submitMutation.isPending}
            data-testid="button-sign-submit"
          >
            <PenLine className="h-5 w-5 mr-2" />
            {submitMutation.isPending ? "Submitting..." : "I Agree & Sign Document"}
          </Button>
          {submitMutation.isError && (
            <p className="text-xs text-destructive text-center mt-2">
              {(submitMutation.error as Error)?.message || "Failed to submit. Please try again."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
